import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { IconButton, LoadingIndicator, Tooltip } from "@digital-services/ui";

import { ApplicationUser } from "@applications/models/ApplicationUser";
import { OrganizationStatus } from "@organization/models/OrganizationDetails";
import { useGetOrganizationDetails } from "@organization/services/use-organization";

import { useDrawerWithConfirm } from "@hooks/useDrawerWithConfirm";
import { useUserContext } from "@hooks/useUserContext";
import EditIcon from "@icons/edit.svg?react";

import { EditApplicationUser } from "./EditApplicationUser";

interface Props {
  applicationId: string;
  user: ApplicationUser;
}

const EditApplicationUserButton: FunctionComponent<Props> = ({
  applicationId,
  user
}) => {
  const { user: currentUser } = useUserContext();
  const { organization, isLoading } = useGetOrganizationDetails();
  const { t } = useTranslation(undefined, { keyPrefix: "application.users" });

  const { showDrawer, hideDrawer } = useDrawerWithConfirm();

  const editApplicationUser = (): void => {
    showDrawer({
      title: t("edit.title"),
      children: (
        <EditApplicationUser
          userId={user.userId}
          applicationId={applicationId}
          userDisplayName={user.displayName}
          onCancel={hideDrawer}
          onSuccess={() => {
            hideDrawer();
          }}
        />
      )
    });
  };

  if (!currentUser.isAdministrator) return null;

  const orgInactive = organization?.status === OrganizationStatus.Inactive;

  return (
    <Tooltip message={orgInactive ? "" : t("edit.tooltip")}>
      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <IconButton
          disabled={orgInactive}
          onClick={editApplicationUser}
          data-testid={`edit-user-button-${applicationId}-${user.userId}`}
        >
          <EditIcon />
        </IconButton>
      )}
    </Tooltip>
  );
};

export default EditApplicationUserButton;
